// knowledge/src/languages/index.js
import { getLogger } from '@daitanjs/development';

const languageDataLogger = getLogger('daitan-knowledge-languages');

languageDataLogger.info(
  'Language data module loaded. Contains static data for various languages.'
);

// Review of the data structure for languageData:
// - The main structure is an object where keys are ISO 639-1 language codes (e.g., "en", "es").
// - Each language entry is an object with various properties:
//   - "ISO": The ISO 639-1 code (matches the key, slightly redundant but fine).
//   - "name": An object containing translations of the language's name into other languages.
//     - Keys are ISO 639-1 codes of the translation target language.
//     - Values are the translated name.
//   - "localName": The name of the language in its own script/language.
//   - "countries": An array of ISO 3166-1 alpha-2 country codes where the language is primarily spoken.
//   - "speakers": Estimated number of speakers (integer).
//   - "direction": "LTR" (Left-to-Right) or "RTL" (Right-to-Left).
//   - "script": The primary writing script used.
//   - "difficulty": A numeric value (original range/meaning unclear, e.g., 10 for English, 2 for Chinese). Needs definition if used programmatically.
//   - "vocabulary": Estimated vocabulary size (integer).
//
// Structural Soundness & Potential Improvements:
// - Consistency: Most fields are consistent. The "difficulty" field's scale/meaning should be documented or standardized if it's to be used for comparisons.
// - Data Accuracy/Completeness: Speaker counts, vocabulary sizes are estimates and can become outdated. Country lists might not be exhaustive for languages spoken in many regions.
// - "name" object: Using ISO codes as keys for translations is good.
// - No dynamic code to refactor here, it's static data.

// The original data is extensive. For this refactoring step, the primary action is to ensure
// it's exported correctly.

export const languageData = {
  en: {
    ISO: 'en',
    name: {
      en: 'English',
      es: 'Inglés',
      nl: 'Engels',
      fr: 'Anglais',
      de: 'Englisch',
      it: 'Inglese',
      ru: 'Английский',
      pt: 'Inglês',
      pl: 'Angielski',
      ro: 'Engleză',
      sv: 'Engelska',
    },
    localName: 'English',
    countries: ['US', 'GB', 'CA', 'AU', 'IE', 'NZ', 'ZA'],
    speakers: 1132000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 10,
    vocabulary: 1000000,
  },
  es: {
    ISO: 'es',
    name: {
      en: 'Spanish',
      es: 'Español',
      nl: 'Spaans',
      fr: 'Espagnol',
      de: 'Spanisch',
      it: 'Spagnolo',
      ru: 'Испанский',
      pt: 'Espanhol',
      pl: 'Hiszpański',
      ro: 'Spaniolă',
      sv: 'Spanska',
    },
    localName: 'Español',
    countries: ['ES', 'MX', 'AR', 'CO', 'PE', 'VE', 'CL', 'EC', 'GT', 'CU'],
    speakers: 559000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 8,
    vocabulary: 150000,
  },
  nl: {
    ISO: 'nl',
    name: {
      en: 'Dutch',
      es: 'Holandés',
      nl: 'Nederlands',
      fr: 'Néerlandais',
      de: 'Niederländisch',
      it: 'Olandese',
      ru: 'Голландский',
      pt: 'Holandês',
      pl: 'Holenderski',
      ro: 'Olandeză',
      sv: 'Holländska',
    },
    localName: 'Nederlands',
    countries: ['NL', 'BE', 'SR'],
    speakers: 23000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 200000,
  },
  de: {
    ISO: 'de',
    name: {
      en: 'German',
      es: 'Alemán',
      nl: 'Duits',
      fr: 'Allemand',
      de: 'Deutsch',
      it: 'Tedesco',
      ru: 'Немецкий',
      pt: 'Alemão',
      pl: 'Niemiecki',
      ro: 'Germană',
      sv: 'Tyska',
    },
    localName: 'Deutsch',
    countries: ['DE', 'AT', 'CH'],
    speakers: 92000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 330000,
  },
  fr: {
    ISO: 'fr',
    name: {
      en: 'French',
      es: 'Francés',
      nl: 'Frans',
      fr: 'Français',
      de: 'Französisch',
      it: 'Francese',
      ru: 'Французский',
      pt: 'Francês',
      pl: 'Francuski',
      ro: 'Franceză',
      sv: 'Franska',
    },
    localName: 'Français',
    countries: ['FR', 'BE', 'CH', 'CA', 'LU'],
    speakers: 78000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 200000,
  },
  it: {
    ISO: 'it',
    name: {
      en: 'Italian',
      es: 'Italiano',
      nl: 'Italiaans',
      fr: 'Italien',
      de: 'Italienisch',
      it: 'Italiano',
      ru: 'Итальянский',
      pt: 'Italiano',
      pl: 'Włoski',
      ro: 'Italiană',
      sv: 'Italienska',
    },
    localName: 'Italiano',
    countries: ['IT', 'CH'],
    speakers: 65000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 250000,
  },
  hu: {
    ISO: 'hu',
    name: {
      en: 'Hungarian',
      es: 'Húngaro',
      nl: 'Hongaars',
      fr: 'Hongrois',
      de: 'Ungarisch',
      it: 'Ungherese',
      ru: 'Венгерский',
      pt: 'Húngaro',
      pl: 'Węgierski',
      ro: 'Maghiară',
      sv: 'Ungerska',
    },
    localName: 'Magyar',
    countries: ['HU'],
    speakers: 13000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 7,
    vocabulary: 200000,
  },
  pl: {
    ISO: 'pl',
    name: {
      en: 'Polish',
      es: 'Polaco',
      nl: 'Pools',
      fr: 'Polonais',
      de: 'Polnisch',
      it: 'Polacco',
      ru: 'Польский',
      pt: 'Polonês',
      pl: 'Polski',
      ro: 'Poloneză',
      sv: 'Polska',
    },
    localName: 'Polski',
    countries: ['PL'],
    speakers: 45000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 250000,
  },
  cs: {
    ISO: 'cs',
    name: {
      en: 'Czech',
      es: 'Checo',
      nl: 'Tsjechisch',
      fr: 'Tchèque',
      de: 'Tschechisch',
      it: 'Ceco',
      ru: 'Чешский',
      pt: 'Checo',
      pl: 'Czeski',
      ro: 'Cehă',
      sv: 'Tjeckiska',
    },
    localName: 'Čeština',
    countries: ['CZ'],
    speakers: 10000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 200000,
  },
  sk: {
    ISO: 'sk',
    name: {
      en: 'Slovak',
      es: 'Eslovaco',
      nl: 'Slowaaks',
      fr: 'Slovaque',
      de: 'Slowakisch',
      it: 'Slovacco',
      ru: 'Словацкий',
      pt: 'Eslovaco',
      pl: 'Słowacki',
      ro: 'Slovacă',
      sv: 'Slovakiska',
    },
    localName: 'Slovenčina',
    countries: ['SK'],
    speakers: 5500000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 200000,
  },
  fi: {
    ISO: 'fi',
    name: {
      en: 'Finnish',
      es: 'Finlandés',
      nl: 'Fins',
      fr: 'Finnois',
      de: 'Finnisch',
      it: 'Finlandese',
      ru: 'Финский',
      pt: 'Finlandês',
      pl: 'Fiński',
      ro: 'Finlandeză',
      sv: 'Finska',
    },
    localName: 'Suomi',
    countries: ['FI'],
    speakers: 5500000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 7,
    vocabulary: 100000,
  },
  lv: {
    ISO: 'lv',
    name: {
      en: 'Latvian',
      es: 'Letón',
      nl: 'Lets',
      fr: 'Letton',
      de: 'Lettisch',
      it: 'Lettone',
      ru: 'Латышский',
      pt: 'Letão',
      pl: 'Łotewski',
      ro: 'Letonă',
      sv: 'Lettiska',
    },
    localName: 'Latviešu',
    countries: ['LV'],
    speakers: 1900000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 7,
    vocabulary: 90000,
  },
  lt: {
    ISO: 'lt',
    name: {
      en: 'Lithuanian',
      es: 'Lituano',
      nl: 'Litouws',
      fr: 'Lituanien',
      de: 'Litauisch',
      it: 'Lituano',
      ru: 'Литовский',
      pt: 'Lituano',
      pl: 'Litewski',
      ro: 'Lituaniană',
      sv: 'Litauiska',
    },
    localName: 'Lietuvių',
    countries: ['LT'],
    speakers: 2800000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 7,
    vocabulary: 110000,
  },
  et: {
    ISO: 'et',
    name: {
      en: 'Estonian',
      es: 'Estonio',
      nl: 'Estisch',
      fr: 'Estonien',
      de: 'Estnisch',
      it: 'Estone',
      ru: 'Эстонский',
      pt: 'Estoniano',
      pl: 'Estoński',
      ro: 'Estoniană',
      sv: 'Estniska',
    },
    localName: 'Eesti',
    countries: ['EE'],
    speakers: 1200000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 100000,
  },
  sr: {
    ISO: 'sr',
    name: {
      en: 'Serbian',
      es: 'Serbio',
      nl: 'Servisch',
      fr: 'Serbe',
      de: 'Serbisch',
      it: 'Serbo',
      ru: 'Сербский',
      pt: 'Sérvio',
      pl: 'Serbski',
      ro: 'Sârbă',
      sv: 'Serbiska',
    },
    localName: 'Српски',
    countries: ['RS', 'BA', 'ME'],
    speakers: 9000000,
    direction: 'LTR',
    script: 'Cyrillic, Latin',
    difficulty: 6,
    vocabulary: 150000,
  },
  hr: {
    ISO: 'hr',
    name: {
      en: 'Croatian',
      es: 'Croata',
      nl: 'Kroatisch',
      fr: 'Croate',
      de: 'Kroatisch',
      it: 'Croato',
      ru: 'Хорватский',
      pt: 'Croata',
      pl: 'Chorwacki',
      ro: 'Croată',
      sv: 'Kroatiska',
    },
    localName: 'Hrvatski',
    countries: ['HR', 'BA'],
    speakers: 5000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 150000,
  },
  sl: {
    ISO: 'sl',
    name: {
      en: 'Slovenian',
      es: 'Esloveno',
      nl: 'Sloveens',
      fr: 'Slovène',
      de: 'Slowenisch',
      it: 'Sloveno',
      ru: 'Словенский',
      pt: 'Esloveno',
      pl: 'Słoweński',
      ro: 'Slovenă',
      sv: 'Slovenska',
    },
    localName: 'Slovenščina',
    countries: ['SI'],
    speakers: 2200000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 6,
    vocabulary: 100000,
  },
  bg: {
    ISO: 'bg',
    name: {
      en: 'Bulgarian',
      es: 'Búlgaro',
      nl: 'Bulgaars',
      fr: 'Bulgare',
      de: 'Bulgarisch',
      it: 'Bulgaro',
      ru: 'Болгарский',
      pt: 'Búlgaro',
      pl: 'Bułgarski',
      ro: 'Bulgărească',
      sv: 'Bulgariska',
    },
    localName: 'Български',
    countries: ['BG'],
    speakers: 7000000,
    direction: 'LTR',
    script: 'Cyrillic',
    difficulty: 6,
    vocabulary: 150000,
  },
  ro: {
    ISO: 'ro',
    name: {
      en: 'Romanian',
      es: 'Rumano',
      nl: 'Roemeens',
      fr: 'Roumain',
      de: 'Rumänisch',
      it: 'Rumeno',
      ru: 'Румынский',
      pt: 'Romeno',
      pl: 'Rumuński',
      ro: 'Română',
      sv: 'Rumänska',
    },
    localName: 'Română',
    countries: ['RO', 'MD'],
    speakers: 24000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 150000,
  },
  el: {
    ISO: 'el',
    name: {
      en: 'Greek',
      es: 'Griego',
      nl: 'Grieks',
      fr: 'Grec',
      de: 'Griechisch',
      it: 'Greco',
      ru: 'Греческий',
      pt: 'Grego',
      pl: 'Grecki',
      ro: 'Greacă',
      sv: 'Grekiska',
    },
    localName: 'Ελληνικά',
    countries: ['GR', 'CY'],
    speakers: 13000000,
    direction: 'LTR',
    script: 'Greek',
    difficulty: 6,
    vocabulary: 300000,
  },
  bn: {
    ISO: 'bn',
    name: {
      en: 'Bengali',
      es: 'Bengalí',
      nl: 'Bengaals',
      fr: 'Bengali',
      de: 'Bengalisch',
      it: 'Bengalese',
      ru: 'Бенгальский',
      pt: 'Bengali',
      pl: 'Bengalski',
      ro: 'Bengaleză',
      sv: 'Bengaliska',
    },
    localName: 'বাংলা',
    countries: ['BD', 'IN'],
    speakers: 300000000,
    direction: 'LTR',
    script: 'Bengali',
    difficulty: 6,
    vocabulary: 120000,
  },
  ur: {
    ISO: 'ur',
    name: {
      en: 'Urdu',
      es: 'Urdú',
      nl: 'Urdu',
      fr: 'Ourdou',
      de: 'Urdu',
      it: 'Urdu',
      ru: 'Урду',
      pt: 'Urdu',
      pl: 'Urdu',
      ro: 'Urdu',
      sv: 'Urdu',
    },
    localName: 'اردو',
    countries: ['PK', 'IN'],
    speakers: 230000000,
    direction: 'RTL',
    script: 'Perso-Arabic',
    difficulty: 6,
    vocabulary: 120000,
  },
  ta: {
    ISO: 'ta',
    name: {
      en: 'Tamil',
      es: 'Tamil',
      nl: 'Tamil',
      fr: 'Tamoul',
      de: 'Tamil',
      it: 'Tamil',
      ru: 'Тамильский',
      pt: 'Tâmil',
      pl: 'Tamilski',
      ro: 'Tamilă',
      sv: 'Tamilska',
    },
    localName: 'தமிழ்',
    countries: ['IN', 'LK', 'MY', 'SG'],
    speakers: 75000000,
    direction: 'LTR',
    script: 'Tamil',
    difficulty: 7,
    vocabulary: 200000,
  },
  vi: {
    ISO: 'vi',
    name: {
      en: 'Vietnamese',
      es: 'Vietnamita',
      nl: 'Vietnamees',
      fr: 'Vietnamien',
      de: 'Vietnamesisch',
      it: 'Vietnamita',
      ru: 'Вьетнамский',
      pt: 'Vietnamita',
      pl: 'Wietnamski',
      ro: 'Vietnameză',
      sv: 'Vietnamesiska',
    },
    localName: 'Tiếng Việt',
    countries: ['VN'],
    speakers: 85000000,
    direction: 'LTR',
    script: 'Latin (Vietnamese alphabet)',
    difficulty: 5,
    vocabulary: 80000,
  },
  th: {
    ISO: 'th',
    name: {
      en: 'Thai',
      es: 'Tailandés',
      nl: 'Thais',
      fr: 'Thaïlandais',
      de: 'Thailändisch',
      it: 'Tailandese',
      ru: 'Тайский',
      pt: 'Tailandês',
      pl: 'Tajski',
      ro: 'Thailandeză',
      sv: 'Thailändska',
    },
    localName: 'ไทย',
    countries: ['TH'],
    speakers: 70000000,
    direction: 'LTR',
    script: 'Thai',
    difficulty: 7,
    vocabulary: 100000,
  },
  sw: {
    ISO: 'sw',
    name: {
      en: 'Swahili',
      es: 'Suajili',
      nl: 'Swahili',
      fr: 'Swahili',
      de: 'Suaheli',
      it: 'Swahili',
      ru: 'Суахили',
      pt: 'Suaíli',
      pl: 'Suahili',
      ro: 'Swahili',
      sv: 'Swahili',
    },
    localName: 'Kiswahili',
    countries: ['TZ', 'KE', 'UG', 'CD'],
    speakers: 60000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 50000,
  },
  fil: {
    ISO: 'fil',
    name: {
      en: 'Filipino',
      es: 'Filipino',
      nl: 'Filipijns',
      fr: 'Philippin',
      de: 'Filipino',
      it: 'Filippino',
      ru: 'Филиппинский',
      pt: 'Filipino',
      pl: 'Filipiński',
      ro: 'Filipineză',
      sv: 'Filippinska',
    },
    localName: 'Filipino',
    countries: ['PH'],
    speakers: 80000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 120000,
  },
  pa: {
    ISO: 'pa',
    name: {
      en: 'Punjabi',
      es: 'Panyabí',
      nl: 'Punjabi',
      fr: 'Pendjabi',
      de: 'Pandschabi',
      it: 'Punjabi',
      ru: 'Панджаби',
      pt: 'Punjabi',
      pl: 'Pendżabski',
      ro: 'Punjabi',
      sv: 'Punjabi',
    },
    localName: 'ਪੰਜਾਬੀ',
    countries: ['IN', 'PK'],
    speakers: 125000000,
    direction: 'LTR',
    script: 'Gurmukhi (India), Shahmukhi (Pakistan)',
    difficulty: 6,
    vocabulary: 120000,
  },
  uk: {
    ISO: 'uk',
    name: {
      en: 'Ukrainian',
      es: 'Ucraniano',
      nl: 'Oekraïens',
      fr: 'Ukrainien',
      de: 'Ukrainisch',
      it: 'Ucraino',
      ru: 'Украинский',
      pt: 'Ucraniano',
      pl: 'Ukraiński',
      ro: 'Ucraineană',
      sv: 'Ukrainska',
    },
    localName: 'Українська',
    countries: ['UA'],
    speakers: 45000000,
    direction: 'LTR',
    script: 'Cyrillic',
    difficulty: 6,
    vocabulary: 250000,
  },
  fa: {
    ISO: 'fa',
    name: {
      en: 'Persian',
      es: 'Persa',
      nl: 'Perzisch',
      fr: 'Persan',
      de: 'Persisch',
      it: 'Persiano',
      ru: 'Персидский',
      pt: 'Persa',
      pl: 'Perski',
      ro: 'Persană',
      sv: 'Persiska',
    },
    localName: 'فارسی',
    countries: ['IR', 'AF', 'TJ'],
    speakers: 110000000,
    direction: 'RTL',
    script: 'Perso-Arabic',
    difficulty: 5,
    vocabulary: 200000,
  },
  ms: {
    ISO: 'ms',
    name: {
      en: 'Malay',
      es: 'Malayo',
      nl: 'Maleis',
      fr: 'Malais',
      de: 'Malaiisch',
      it: 'Malese',
      ru: 'Малайский',
      pt: 'Malaio',
      pl: 'Malajski',
      ro: 'Malaysiană',
      sv: 'Malajiska',
    },
    localName: 'Bahasa Melayu',
    countries: ['MY', 'ID', 'BN'],
    speakers: 250000000,
    direction: 'LTR',
    script: 'Latin (Jawi in some regions)',
    difficulty: 5,
    vocabulary: 150000,
  },
  tk: {
    ISO: 'tk',
    name: {
      en: 'Turkmen',
      es: 'Turcomano',
      nl: 'Turkmeens',
      fr: 'Turkmène',
      de: 'Turkmenisch',
      it: 'Turcomanno',
      ru: 'Туркменский',
      pt: 'Turcomano',
      pl: 'Turkmeński',
      ro: 'Turkmenă',
      sv: 'Turkmenska',
    },
    localName: 'Türkmençe',
    countries: ['TM'],
    speakers: 9000000,
    direction: 'LTR',
    script: 'Latin (Cyrillic and Arabic in some regions)',
    difficulty: 7,
    vocabulary: 100000,
  },
  he: {
    ISO: 'he',
    name: {
      en: 'Hebrew',
      es: 'Hebreo',
      nl: 'Hebreeuws',
      fr: 'Hébreu',
      de: 'Hebräisch',
      it: 'Ebraico',
      ru: 'Иврит',
      pt: 'Hebraico',
      pl: 'Hebrajski',
      ro: 'Ebraică',
      sv: 'Hebreiska',
    },
    localName: 'עברית',
    countries: ['IL'],
    speakers: 9000000,
    direction: 'RTL',
    script: 'Hebrew',
    difficulty: 6,
    vocabulary: 80000,
  },
  pt: {
    ISO: 'pt',
    name: {
      en: 'Portuguese',
      es: 'Portugués',
      nl: 'Portugees',
      fr: 'Portugais',
      de: 'Portugiesisch',
      it: 'Portoghese',
      ru: 'Португальский',
      pt: 'Português',
      pl: 'Portugalski',
      ro: 'Portugheză',
      sv: 'Portugisiska',
    },
    localName: 'Português',
    countries: ['PT', 'BR', 'AO', 'MZ'],
    speakers: 220000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 250000,
  },
  ru: {
    ISO: 'ru',
    name: {
      en: 'Russian',
      es: 'Ruso',
      nl: 'Russisch',
      fr: 'Russe',
      de: 'Russisch',
      it: 'Russo',
      ru: 'Русский',
      pt: 'Russo',
      pl: 'Rosyjski',
      ro: 'Rusă',
      sv: 'Ryska',
    },
    localName: 'Русский',
    countries: ['RU', 'BY', 'KZ'],
    speakers: 258000000,
    direction: 'LTR',
    script: 'Cyrillic',
    difficulty: 4,
    vocabulary: 200000,
  },
  da: {
    ISO: 'da',
    name: {
      en: 'Danish',
      es: 'Danés',
      nl: 'Deens',
      fr: 'Danois',
      de: 'Dänisch',
      it: 'Danese',
      ru: 'Датский',
      pt: 'Dinamarquês',
      pl: 'Duński',
      ro: 'Daneză',
      sv: 'Danska',
    },
    localName: 'Dansk',
    countries: ['DK'],
    speakers: 6000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 200000,
  },
  id: {
    ISO: 'id',
    name: {
      en: 'Indonesian',
      es: 'Indonesio',
      nl: 'Indonesisch',
      fr: 'Indonésien',
      de: 'Indonesisch',
      it: 'Indonesiano',
      ru: 'Индонезийский',
      pt: 'Indonésio',
      pl: 'Indonezyjski',
      ro: 'Indoneziană',
      sv: 'Indonesiska',
    },
    localName: 'Bahasa Indonesia',
    countries: ['ID'],
    speakers: 43000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 7,
    vocabulary: 120000,
  },
  tr: {
    ISO: 'tr',
    name: {
      en: 'Turkish',
      es: 'Turco',
      nl: 'Turks',
      fr: 'Turc',
      de: 'Türkisch',
      it: 'Turco',
      ru: 'Турецкий',
      pt: 'Turco',
      pl: 'Turecki',
      ro: 'Turcă',
      sv: 'Turkiska',
    },
    localName: 'Türkçe',
    countries: ['TR'],
    speakers: 75000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 4,
    vocabulary: 150000,
  },
  no: {
    ISO: 'no',
    name: {
      en: 'Norwegian',
      es: 'Noruego',
      nl: 'Noors',
      fr: 'Norvégien',
      de: 'Norwegisch',
      it: 'Norvegese',
      ru: 'Норвежский',
      pt: 'Norueguês',
      pl: 'Norweski',
      ro: 'Norvegiană',
      sv: 'Norska',
    },
    localName: 'Norsk',
    countries: ['NO'],
    speakers: 5300000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 200000,
  },
  ar: {
    ISO: 'ar',
    name: {
      en: 'Arabic',
      es: 'Árabe',
      nl: 'Arabisch',
      fr: 'Arabe',
      de: 'Arabisch',
      it: 'Arabo',
      ru: 'Арабский',
      pt: 'Árabe',
      pl: 'Arabski',
      ro: 'Arabă',
      sv: 'Arabiska',
    },
    localName: 'العربية',
    countries: ['SA', 'IQ', 'EG', 'SY', 'MA', 'TN', 'DZ', 'LY'],
    speakers: 310000000,
    direction: 'RTL',
    script: 'Arabic',
    difficulty: 3,
    vocabulary: 120000,
  },
  zh: {
    ISO: 'zh',
    name: {
      en: 'Chinese',
      es: 'Chino',
      nl: 'Chinees',
      fr: 'Chinois',
      de: 'Chinesisch',
      it: 'Cinese',
      ru: 'Китайский',
      pt: 'Chinês',
      pl: 'Chiński',
      ro: 'Chineză',
      sv: 'Kinesiska',
    },
    localName: '中文',
    countries: ['CN', 'SG', 'TW'],
    speakers: 1117000000,
    direction: 'LTR',
    script: 'Chinese characters',
    difficulty: 2,
    vocabulary: 50000,
  },
  ja: {
    ISO: 'ja',
    name: {
      en: 'Japanese',
      es: 'Japonés',
      nl: 'Japans',
      fr: 'Japonais',
      de: 'Japanisch',
      it: 'Giapponese',
      ru: 'Японский',
      pt: 'Japonês',
      pl: 'Japoński',
      ro: 'Japoneză',
      sv: 'Japanska',
    },
    localName: '日本語',
    countries: ['JP'],
    speakers: 128000000,
    direction: 'LTR',
    script: 'Kanji, Hiragana, Katakana',
    difficulty: 3,
    vocabulary: 500000,
  },
  ko: {
    ISO: 'ko',
    name: {
      en: 'Korean',
      es: 'Coreano',
      nl: 'Koreaans',
      fr: 'Coréen',
      de: 'Koreanisch',
      it: 'Coreano',
      ru: 'Корейский',
      pt: 'Coreano',
      pl: 'Koreański',
      ro: 'Coreeană',
      sv: 'Koreanska',
    },
    localName: '한국어',
    countries: ['KR'],
    speakers: 77000000,
    direction: 'LTR',
    script: 'Hangul',
    difficulty: 4,
    vocabulary: 210000,
  },
  hi: {
    ISO: 'hi',
    name: {
      en: 'Hindi',
      es: 'Hindi',
      nl: 'Hindi',
      fr: 'Hindi',
      de: 'Hindi',
      it: 'Hindi',
      ru: 'Хинди',
      pt: 'Hindi',
      pl: 'Hindi',
      ro: 'Hindi',
      sv: 'Hindi',
    },
    localName: 'हिन्दी',
    countries: ['IN'],
    speakers: 341000000,
    direction: 'LTR',
    script: 'Devanagari',
    difficulty: 7,
    vocabulary: 120000,
  },
  sv: {
    ISO: 'sv',
    name: {
      en: 'Swedish',
      es: 'Sueco',
      nl: 'Zweeds',
      fr: 'Suédois',
      de: 'Schwedisch',
      it: 'Svedese',
      ru: 'Шведский',
      pt: 'Sueco',
      pl: 'Szwedzki',
      ro: 'Suedeză',
      sv: 'Svenska',
    },
    localName: 'Svenska',
    countries: ['SE', 'FI'],
    speakers: 10000000,
    direction: 'LTR',
    script: 'Latin',
    difficulty: 5,
    vocabulary: 125000,
  },
};
